/**
 *
 * @module watchlistExtensions
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import WatchlistQuery from './watchlistQuery';
import RemoveFromWatchlistQuery from './removeFromWatchlistQuery';
import AddToWatchlistQuery from './addToWatchlistQuery';
import ClearWatchlistQuery from './clearWatchlistQuery';

const GraphQlBuilder = Services.Content.GraphQlBuilder;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @function WatchlistExtensions
 * @param {SDK.Content.ContentManager} ContentManager
 * @desc Adds extension methods onto the ContentManager prototype.
 *
 */
export default (ContentManager) => {

    /**
     *
     * @access public
     * @param {GraphQlQuery} query
     * @param {String} [contentTransactionId] - An application developer-provided value to be included with the HTTP
     * request as the value of the `X-Content-Transaction-ID` header.
     * @returns {Promise<Object>}
     *
     */
    ContentManager.prototype.getWatchlist = function (query, contentTransactionId) {

        const errorMessage = {
            method: 'getWatchlist(query, contentTransactionId)',
            get query() {
                return `${this.method} needs GraphQlBuilder instance`;
            },
            get contentTransactionId() {
                return `${this.method} contentTransactionId should be a String with length`;
            }
        };

        instanceStrict(query, GraphQlBuilder, errorMessage.query, InvalidArgumentException);

        if (Check.assigned(contentTransactionId)) {
            nonEmptyString(contentTransactionId, errorMessage.contentTransactionId, InvalidArgumentException);
        }

        const watchlistQuery = new WatchlistQuery(query);

        return this.query({
            query: watchlistQuery,
            contentTransactionId
        });

    };

    /**
     *
     * @access public
     * @param {Array<String>} contentIds
     * @param {String} [contentTransactionId] - An application developer-provided value to be included with the HTTP
     * request as the value of the `X-Content-Transaction-ID` header.
     * @return {Promise<Void>}
     *
     */
    ContentManager.prototype.removeFromWatchlist = function (contentIds, contentTransactionId) {

        const errorMessage = {
            method: 'removeFromWatchlist(contentIds)',
            get contentIds() {
                return `${this.method} needs Array of Strings`;
            },
            get contentTransactionId() {
                return `${this.method} contentTransactionId should be a String with length`;
            }
        };

        array.of.nonEmptyString(contentIds, errorMessage.contentIds, InvalidArgumentException);

        if (Check.assigned(contentTransactionId)) {
            nonEmptyString(contentTransactionId, errorMessage.contentTransactionId, InvalidArgumentException);
        }

        const removeFromWatchlistQuery = new RemoveFromWatchlistQuery(contentIds);

        return this.query({
            query: removeFromWatchlistQuery,
            contentTransactionId
        });

    };

    /**
     *
     * @access public
     * @param {Array<String>} contentIds
     * @param {String} [contentTransactionId] - An application developer-provided value to be included with the HTTP
     * request as the value of the `X-Content-Transaction-ID` header.
     * @returns {Promise<Void>}
     *
     */
    ContentManager.prototype.addToWatchlist = function (contentIds, contentTransactionId) {

        const errorMessage = {
            method: 'addToWatchlist(contentIds)',
            get contentIds() {
                return `${this.method} needs Array of Strings`;
            },
            get contentTransactionId() {
                return `${this.method} contentTransactionId should be a String with length`;
            }
        };

        array.of.nonEmptyString(contentIds, errorMessage.contentIds, InvalidArgumentException);

        if (Check.assigned(contentTransactionId)) {
            nonEmptyString(contentTransactionId, errorMessage.contentTransactionId, InvalidArgumentException);
        }

        const addToWatchlistQuery = new AddToWatchlistQuery(contentIds);

        return this.query({
            query: addToWatchlistQuery,
            contentTransactionId
        });

    };

    /**
     *
     * @access public
     * @returns {Promise<Void>}
     *
     */
    ContentManager.prototype.clearWatchlist = function () {

        const clearWatchlistQuery = new ClearWatchlistQuery();

        return this.query(clearWatchlistQuery);

    };

};



// WEBPACK FOOTER //
// ./src/content/watchlist/watchlistExtensions.js